/**
 * Rescission Period Utilities
 * Handles the 3-business-day right of rescission window.
 */

import type { RescissionStatus } from "./types";

const RESCISSION_WINDOW_DAYS = 3;

/** Add business days (M-F) to a date */
export function addBusinessDays(start: Date, days: number): Date {
  const result = new Date(start);
  let added = 0;
  while (added < days) {
    result.setDate(result.getDate() + 1);
    const dow = result.getDay();
    if (dow !== 0 && dow !== 6) added++;
  }
  return result;
}

/** Calculate rescission end date (3 business days from sale close) */
export function calculateRescissionEnd(wonAt: string): string {
  const wonDate = new Date(wonAt);
  return addBusinessDays(wonDate, 3).toISOString();
}

/** Calculate rescission end date from won_at using a 3 calendar-day window */
export function calculateRescissionEndFromWonAt(wonAt: string): string {
  const wonDate = new Date(wonAt);
  return new Date(wonDate.getTime() + RESCISSION_WINDOW_DAYS * 24 * 60 * 60 * 1000).toISOString();
}

/** Determine rescission status from won_at using a 3 calendar-day window */
export function getRescissionStatusFromWonAt(wonAt?: string): RescissionStatus {
  if (!wonAt) return "Not Applicable";
  const now = new Date();
  const end = new Date(calculateRescissionEndFromWonAt(wonAt));
  return now < end ? "In Rescission" : "Cleared for Production";
}

/** Check if won_at is still inside the 3-day rescission window */
export function isInRescissionFromWonAt(wonAt?: string): boolean {
  return getRescissionStatusFromWonAt(wonAt) === "In Rescission";
}

/** Get remaining calendar days in rescission window from won_at */
export function getRescissionDaysRemainingFromWonAt(wonAt?: string): number {
  if (!wonAt) return 0;
  const now = new Date();
  const end = new Date(calculateRescissionEndFromWonAt(wonAt));
  if (now >= end) return 0;
  const msRemaining = end.getTime() - now.getTime();
  return Math.max(1, Math.ceil(msRemaining / (24 * 60 * 60 * 1000)));
}

/** Determine current rescission status */
export function getRescissionStatus(rescissionEndsAt?: string): RescissionStatus {
  if (!rescissionEndsAt) return "Not Applicable";
  const now = new Date();
  const end = new Date(rescissionEndsAt);
  return now < end ? "In Rescission" : "Cleared for Production";
}

/** Check if a job is currently in rescission period */
export function isInRescission(rescissionEndsAt?: string): boolean {
  return getRescissionStatus(rescissionEndsAt) === "In Rescission";
}

/** Get remaining business days in rescission period */
export function getRescissionDaysRemaining(rescissionEndsAt?: string): number {
  if (!rescissionEndsAt) return 0;
  const now = new Date();
  const end = new Date(rescissionEndsAt);
  if (now >= end) return 0;
  let days = 0;
  const cursor = new Date(now);
  while (cursor < end) {
    cursor.setDate(cursor.getDate() + 1);
    const dow = cursor.getDay();
    if (dow !== 0 && dow !== 6) days++;
  }
  return days;
}

/** Format rescission end date for display */
export function formatRescissionEnd(rescissionEndsAt?: string): string {
  if (!rescissionEndsAt) return "";
  return new Date(rescissionEndsAt).toLocaleDateString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}
